import { type AlternativeMode } from '../contracts/decors';
import {
  PaintListResponseSchema,
  PaintSchema,
  type Paint,
  type PaintListResponse,
} from '../contracts/paint';
import {
  PaintMatchRequestSchema,
  PaintMatchResponseSchema,
  type PaintMatchRequest,
  type PaintMatchResponse,
} from '../contracts/paint-match';
import { type PaintBrand } from '../domain/paint-brands';
import { type PaintCategory } from '../domain/paint-categories';
import { type PaintTone } from '../domain/paint-tones';
import { ShopsResponseSchema, type ShopsResponse } from '../contracts/handles';
import type { QueryValue, RequestFn } from './client';

/**
 * Namespace `client.paint` — обёртки над эндпоинтами Блока 3 «Краски»
 * (PRD §6, ADR-0010).
 *
 * Структурно повторяет `api-client/decors.ts`, но **без `kind`-параметра**:
 * paint — один домен (ADR-0010 §1). Endpoint `/api/paint/...` сам несёт
 * информацию о домене через URL.
 *
 * P-009 защита для match — `safeParse` через `.strict()` Zod-схему до
 * отправки запроса. Любой extra-field (`photoBlob`, `base64`, `mask`)
 * → rejected Promise (no fetch). Симметрия с `decors.match`.
 */

/** Loose query input для list — strict валидация в MSW/Edge через PaintListQuerySchema. */
export interface PaintListQueryInput {
  tones?: readonly PaintTone[];
  categories?: readonly PaintCategory[];
  brands?: readonly PaintBrand[];
  ralCodes?: readonly string[];
  cursor?: string;
  limit?: number;
}

export interface PaintApi {
  list(query?: PaintListQueryInput): Promise<PaintListResponse>;
  get(globalId: string): Promise<Paint>;
  match(request: PaintMatchRequest): Promise<PaintMatchResponse>;
  /** Sensitive: требует JWT (401 для гостя). Reuse контракта из Block 4 (ADR-0007). */
  shops(globalId: string): Promise<ShopsResponse>;
  /** Phase 2 stub — 4 random paints same tone (Phase 4 — реальный LAB-shift). */
  alternatives(globalId: string, mode: AlternativeMode): Promise<PaintListResponse>;
}

export function createPaintApi(request: RequestFn): PaintApi {
  return {
    list: (query = {}) =>
      request({
        method: 'GET',
        path: '/api/paint',
        query: queryToParams(query),
        responseSchema: PaintListResponseSchema,
      }),

    get: (globalId) =>
      request({
        method: 'GET',
        path: `/api/paint/${encodeURIComponent(globalId)}`,
        responseSchema: PaintSchema,
      }),

    match: (req) => {
      // P-009 уровня 2 (ADR-0009): даже если type-checker обойдён,
      // .strict() блокирует extra-поля типа photoBlob / base64.
      // Возвращаем rejected Promise (не sync throw) — клиент-API всегда async.
      const parsed = PaintMatchRequestSchema.safeParse(req);
      if (!parsed.success) {
        return Promise.reject(parsed.error);
      }
      return request({
        method: 'POST',
        path: '/api/paint/match',
        body: parsed.data,
        responseSchema: PaintMatchResponseSchema,
      });
    },

    shops: (globalId) =>
      request({
        method: 'GET',
        path: `/api/paint/${encodeURIComponent(globalId)}/shops`,
        responseSchema: ShopsResponseSchema,
        auth: true,
      }),

    alternatives: (globalId, mode) =>
      request({
        method: 'GET',
        path: `/api/paint/${encodeURIComponent(globalId)}/alternatives`,
        query: { mode },
        responseSchema: PaintListResponseSchema,
      }),
  };
}

function queryToParams(q: PaintListQueryInput): Record<string, QueryValue> {
  return {
    tones: q.tones,
    categories: q.categories,
    brands: q.brands,
    ralCodes: q.ralCodes,
    cursor: q.cursor,
    limit: q.limit !== undefined ? Number(q.limit) : undefined,
  };
}
